// skipcq: JS-W1028
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

const TAP_ICON_EMPTY = '/assets/ratings/Tap_0.png';
const TAP_ICON_HALF = '/assets/ratings/Tap_05.png';
const TAP_ICON_FULL = '/assets/ratings/Tap_1.png';

const DEFAULT_MAX_RATING = 5;
const DEFAULT_ICON_SIZE = 20;

/**
 * @param {unknown} value
 * @returns {number | null}
 */
function toNumericRating(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  return value;
}

/**
 * @param {number} rating
 * @param {number} index
 * @returns {string}
 */
function getTapIconForIndex(rating, index) {
  const tapValue = index + 1;
  if (rating >= tapValue) {
    return TAP_ICON_FULL;
  }
  if (rating >= tapValue - 0.5) {
    return TAP_ICON_HALF;
  }
  return TAP_ICON_EMPTY;
}

/**
 * Read-only tap rating bar aligned with mobile IttRatingsBar.
 *
 * @param {object} props
 * @param {number | null | undefined} props.rating
 * @param {number} [props.maxRating]
 * @param {number} [props.iconSize]
 * @param {boolean} [props.showNumeric]
 * @param {boolean} [props.showUnratedLabel]
 * @param {string} [props.unratedLabel]
 * @param {boolean} [props.hideIconsWhenUnrated]
 * @param {'compact' | 'default'} [props.size]
 */
function TapRatingBar({
  rating,
  maxRating = DEFAULT_MAX_RATING,
  iconSize = DEFAULT_ICON_SIZE,
  showNumeric = false,
  showUnratedLabel = false,
  unratedLabel = 'Not yet rated',
  hideIconsWhenUnrated = false,
  size = 'default',
}) {
  const numericRating = toNumericRating(rating);
  const hasRating = numericRating !== null && numericRating > 0;
  const clampedRating = hasRating
    ? Math.min(maxRating, Math.max(0, numericRating))
    : 0;
  const resolvedIconSize = size === 'compact' ? Math.max(14, iconSize - 4) : iconSize;
  const showIcons = hasRating || !hideIconsWhenUnrated;

  const icons = Array.from({ length: maxRating }, (_, index) => (
    <Box
      key={`tap-${index}`}
      component="img"
      src={getTapIconForIndex(clampedRating, index)}
      alt=""
      sx={{
        width: resolvedIconSize,
        height: resolvedIconSize,
        objectFit: 'contain',
        display: 'block',
      }}
    />
  ));

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: size === 'compact' ? 0.25 : 0.5,
        flexWrap: 'nowrap',
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}
      aria-label={
        hasRating
          ? `Overall rating ${clampedRating.toFixed(2)} out of ${maxRating}`
          : unratedLabel
      }
    >
      {showIcons && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
          {icons}
        </Box>
      )}
      {showNumeric && hasRating && (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ ml: showIcons ? 0.5 : 0, whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {`${clampedRating.toFixed(2)}/${maxRating}`}
        </Typography>
      )}
      {showUnratedLabel && !hasRating && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: showIcons ? 0.5 : 0, whiteSpace: 'nowrap' }}
        >
          {unratedLabel}
        </Typography>
      )}
    </Box>
  );
}

TapRatingBar.propTypes = {
  rating: PropTypes.number,
  maxRating: PropTypes.number,
  iconSize: PropTypes.number,
  showNumeric: PropTypes.bool,
  showUnratedLabel: PropTypes.bool,
  unratedLabel: PropTypes.string,
  hideIconsWhenUnrated: PropTypes.bool,
  size: PropTypes.oneOf(['compact', 'default']),
};

export default TapRatingBar;
